/**
 * Environment utility functions
 */

/**
 * Check if we're running in development mode
 * Uses multiple checks to ensure accurate detection
 */
export const isDevelopment = (): boolean => {
  // Primary check: Vite's built-in environment detection
  if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
    return (import.meta as any).env.DEV;
  }
  
  // Fallback check: hostname-based detection for browser environment
  if (typeof window !== 'undefined') {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.startsWith('192.168.') ||
           window.location.hostname.endsWith('.local');
  }
  
  // Server-side fallback (only use process if available)
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  
  // Final fallback - assume production
  return false;
};

/**
 * Get the backend URL based on environment configuration
 */
export const getBackendUrl = (): string => {
  // Always use a relative path. In development, the Vite proxy will handle it.
  // In production, the request goes to the same origin.
  return '';
};

/**
 * Get the frontend URL based on environment configuration
 */
export const getFrontendUrl = (): string => {
  // Check for environment variables
  const frontendHost = (import.meta as any).env?.VITE_FRONTEND_HOST || 'localhost';
  const frontendPort = (import.meta as any).env?.VITE_FRONTEND_PORT || '5173';
  
  if (isDevelopment()) {
    return `http://${frontendHost}:${frontendPort}`;
  }
  
  // In production, use the current origin
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  
  return '';
};

/**
 * Conditionally log messages only in development
 */
export const devLog = (...args: any[]): void => {
  if (isDevelopment()) {
    console.log(...args);
  }
};

/**
 * Conditionally log warnings only in development
 */
export const devWarn = (...args: any[]): void => {
  if (isDevelopment()) {
    console.warn(...args);
  }
};

/**
 * Conditionally log errors only in development
 */
export const devError = (...args: any[]): void => {
  if (isDevelopment()) {
    console.error(...args);
  }
};

/**
 * Create a preview URL with query parameters
 * This is a more reliable way to construct preview URLs than using URL constructor
 */
export const createPreviewUrl = (params: Record<string, string | number | boolean>): string => {
  // Use relative URL instead of absolute URL to avoid routing issues
  const baseUrl = '/preview';
  const searchParams = new URLSearchParams();
  
  // Add all parameters to the URL
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  }
  
  const finalUrl = `${baseUrl}?${searchParams.toString()}`;
  
  return finalUrl;
}; 